/**
 * Cronologia (audit trail) del ticket — prototipo L378-L393.
 *
 * Le etichette restano quelle di `HistoryTimeline.tsx` (`historyLabel`): tutti
 * e tredici i tipi di evento registrati dal backend, comprese le due aggiunte
 * recenti `tags_changed` e `priority_changed`, e la valutazione `rated` che
 * vive SOLO qui (§7.1 n.23). Nessuna voce filtrata, ordine inverso: la piu
 * recente in alto, come oggi.
 *
 * Solo operatori: `toRequesterView` non serve `history`.
 *
 * Veste v4 (T37, `Centro Ticket v4.dc.html` L573-L579, L1145): comprimibile,
 * si vedono le 3 voci piu recenti e «Mostra tutte (N)» / «Mostra meno». Le
 * etichette e l'ordine restano quelli di sopra; nessuna voce e filtrata.
 */
import React, { useId, useState } from 'react';
import { historyLabel } from '../HistoryTimeline';
import { formatDate } from '../constants';
import { useSupportLook } from '../v4/lookContext';
import type { HistoryEntry } from '../../../services/supportApi';

export interface HistoryCardProps {
  history?: HistoryEntry[];
}

/** Voci visibili a cronologia compressa (v4, L1145). */
export const HISTORY_COLLAPSED_COUNT = 3;

const HistoryCard: React.FC<HistoryCardProps> = ({ history }) => {
  const headingId = useId();
  const listId = useId();
  const look = useSupportLook();
  const [showAll, setShowAll] = useState(false);
  const entries = [...(history || [])].reverse();
  if (entries.length === 0) return null;

  const collapsible = look === 'v4' && entries.length > HISTORY_COLLAPSED_COUNT;
  const shown = collapsible && !showAll ? entries.slice(0, HISTORY_COLLAPSED_COUNT) : entries;

  return (
    <section className="s3-card s3-ctx" aria-labelledby={headingId}>
      <h3 id={headingId} className="s3-h3">Cronologia</h3>
      <ol className="s3-tl" id={collapsible ? listId : undefined}>
        {shown.map((e, i) => (
          <li key={`${e.at}-${i}`} className="s3-tl__item">
            <span className="s3-tl__dot" aria-hidden="true" />
            <div className="s3-tl__body">
              <div className="s3-tl__txt">{historyLabel(e)}</div>
              <div className="s3-meta">{formatDate(e.at)}</div>
            </div>
          </li>
        ))}
      </ol>
      {collapsible && (
        <button
          type="button"
          className="s4-ctx__more"
          aria-expanded={showAll}
          aria-controls={listId}
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll ? 'Mostra meno' : `Mostra tutte (${entries.length})`}
        </button>
      )}
    </section>
  );
};

export default HistoryCard;
